import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// Layouts
import RootLayout from './layouts/RootLayout';
import StudentLayout from './layouts/StudentLayout';
import AdminLayout from './layouts/AdminLayout';
import AuthLayout from './layouts/AuthLayout';
import ProtectedRoute from './components/ProtectedRoute';

// Public Pages
import Home from './pages/index';
import AboutPage from './pages/about/index';
import ProductPage from './pages/product/index';
import HowItWorksPage from './pages/how-it-works/index';
import IndustriesPage from './pages/industries/index';
import SolutionsPage from './pages/solutions/index';
import FaqsPage from './pages/faqs/index';
import GetStartedPage from './pages/get-started/index';
import CaseStudiesPage from './pages/case-studies/index';
import CaseStudyPage from './pages/case-studies/[slug]/index';
import SignInPage from './pages/sign-in/[[...sign-in]]/index';
import SignUpPage from './pages/sign-up/[[...sign-up]]/index';
import B2BPage from './pages/b2b/index';
import PricingPage from './pages/pricing/index';
import CookiePolicyPage from './pages/cookie-policy/index';
import PrivacyPolicyPage from './pages/privacy-policy/index';
import TermsOfServicePage from './pages/terms-of-service/index';
import CatalogPage from './pages/catalog/index';
import CatalogCoursePage from './pages/catalog/[courseUuid]/index';
import CheckoutPlanPage from './pages/checkout/[planId]/index';
import CheckoutSuccessPage from './pages/checkout/success/index';
import CheckoutErrorPage from './pages/checkout/error/index';
import AcceptInvitationPage from './pages/invitations/accept/index';
import OnboardingPage from './pages/onboarding/index';
import PlacementQuizPage from './pages/placement-quiz/[courseId]/index';

// Student App Pages
import CoursesPage from './pages/courses/index';
import CourseDetailPage from './pages/courses/[id]/index';
import StudentLessonRoutePage from './pages/courses/[id]/lessons/[lessonId]/index';
import ModuleQuizPage from './pages/courses/[id]/module-quiz/[moduleId]/index';
import CoursePlansPage from './pages/courses/[id]/plans/index';
import CourseQuizPage from './pages/courses/[id]/quiz/index';
import RemedialLessonPage from './pages/courses/remedial/[id]/index';
import CertificatesPage from './pages/certificates/index';
import AccountPage from './pages/account/index';
import StudentPricingPage from './pages/account/pricing/index';
import SettingsPage from './pages/settings/index';

// B2B Organization Pages
import B2BJoinPage from './pages/join/[slug]/index';
import VerifyCertificatePage from './pages/verify/[code]/index';
import PublicLayout from './layouts/PublicLayout';
import IleHarnessPage from './pages/dev/ile-harness';
import B2BOrgDashboardPage from './pages/org/[slug]/dashboard/index';
import B2BRegisterPage from './pages/b2b/register/index';

// Admin Pages
import AdminPage from './pages/admin/index';
import AdminCoursesPage from './pages/admin/courses/index';
import InteractiveCourseBuilderPage from './pages/admin/courses/builder/index';
import LessonBuilderPage from './pages/admin/courses/builder/lesson/[id]/index';
import AdminCanonicalLessonsPage from './pages/admin/courses/canonical-lessons/index';
import AdminCourseDetailPage from './pages/admin/courses/[id]/index';
import AdminModuleQuizPage from './pages/admin/courses/[id]/modules/[moduleId]/quiz/index';
import AdminPaymentsPage from './pages/admin/payments/index';
import AdminDashboardPage from './pages/admin/dashboard/index';
import CourseProgressPage from './pages/admin/dashboard/courses/[id]/progress/index';
import AdminInvitationsPage from './pages/admin/invitations/index';
import AdminMembersPage from './pages/admin/members/index';
import AdminOrganizationsPage from './pages/admin/organizations/index';
import AdminCreditsPage from './pages/admin/credits/index';
import AdminAiModelsPage from './pages/admin/ai-models/index';
import AdminReviewedPage from './pages/admin/reviewed/index';
import AdminReviewsPage from './pages/admin/reviews/index';
import ReviewDetailPage from './pages/admin/reviews/[type]/[id]/index';
import AdminReviewLessonQuizPage from './pages/admin/reviews/lesson/[id]/quiz/index';

export default function App() {
  return (
    <Routes>
      {/* PublicLayout, not RootLayout: an employer checking a printed certificate
          has no account, and RootLayout's AuthProvider blocks render behind
          Clerk's session load. PublicLayout still supplies a QueryClient. */}
      <Route element={<PublicLayout />}>
        <Route path="/verify/:code" element={<VerifyCertificatePage />} />
      </Route>

      <Route element={<RootLayout />}>
        {/* Public Pages */}
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/product" element={<ProductPage />} />
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="/industries" element={<IndustriesPage />} />
        <Route path="/solutions" element={<SolutionsPage />} />
        <Route path="/faqs" element={<FaqsPage />} />
        <Route path="/get-started" element={<GetStartedPage />} />
        <Route path="/case-studies" element={<CaseStudiesPage />} />
        <Route path="/case-studies/:slug" element={<CaseStudyPage />} />
        <Route path="/b2b" element={<B2BPage />} />
        <Route path="/b2b/register" element={<B2BRegisterPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/cookie-policy" element={<CookiePolicyPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/catalog" element={<CatalogPage />} />
        <Route path="/catalog/:courseUuid" element={<CatalogCoursePage />} />
        <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
        <Route path="/checkout/error" element={<CheckoutErrorPage />} />
        <Route path="/checkout/:planId" element={<CheckoutPlanPage />} />
        <Route path="/checkout/tier/:tierId" element={<CheckoutPlanPage />} />
        <Route path="/checkout/package/:packageId" element={<CheckoutPlanPage />} />
        <Route path="/checkout" element={<CheckoutPlanPage />} />
        <Route path="/invitations/accept" element={<AcceptInvitationPage />} />
        <Route path="/invitations/accept/*" element={<AcceptInvitationPage />} />
        <Route path="/join/:slug" element={<B2BJoinPage />} />
        {/* Dev-only: exercises the ILE blocks on fixture content without a
            signed-in learner. Registered only in dev builds, so it is absent
            from production bundles entirely. */}
        {import.meta.env.DEV && (
          <Route path="/dev/ile-harness" element={<IleHarnessPage />} />
        )}

        {/* Authentication Pages */}
        <Route element={<AuthLayout />}>
          <Route path="/sign-in/*" element={<SignInPage />} />
          <Route path="/sign-up/*" element={<SignUpPage />} />
        </Route>

        {/* Protected Routes (Requires Authentication) */}
        <Route element={<ProtectedRoute />}>
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/placement-quiz/:courseId" element={<PlacementQuizPage />} />
          <Route path="/org/:slug/dashboard" element={<B2BOrgDashboardPage />} />

          {/* Student App Routes */}
          <Route element={<StudentLayout />}>
            <Route path="/courses" element={<CoursesPage />} />
            <Route path="/courses/:id" element={<CourseDetailPage />} />
            <Route path="/courses/:id/lessons/:lessonId" element={<StudentLessonRoutePage />} />
            <Route path="/courses/:id/module-quiz/:moduleId" element={<ModuleQuizPage />} />
            <Route path="/courses/:id/plans" element={<CoursePlansPage />} />
            <Route path="/courses/:id/quiz" element={<CourseQuizPage />} />
            <Route path="/courses/remedial/:id" element={<RemedialLessonPage />} />
            <Route path="/certificates" element={<CertificatesPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/user" element={<Navigate to="/account" replace />} />
            <Route path="/account/pricing" element={<StudentPricingPage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Route>

          {/* Admin Dashboard Routes */}
          <Route element={<AdminLayout />}>
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/admin/payments" element={<AdminPaymentsPage />} />
            <Route path="/admin/organizations" element={<AdminOrganizationsPage />} />
            <Route path="/admin/credits" element={<AdminCreditsPage />} />
            <Route path="/admin/ai-models" element={<AdminAiModelsPage />} />
            <Route path="/admin/courses" element={<AdminCoursesPage />} />
            <Route path="/admin/courses/builder" element={<InteractiveCourseBuilderPage />} />
            <Route path="/admin/courses/builder/lesson/:id" element={<LessonBuilderPage />} />
            <Route path="/admin/courses/canonical-lessons" element={<AdminCanonicalLessonsPage />} />
            <Route path="/admin/courses/:id" element={<AdminCourseDetailPage />} />
            <Route path="/admin/courses/:id/modules/:moduleId/quiz" element={<AdminModuleQuizPage />} />
            <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
            <Route path="/admin/dashboard/courses/:id/progress" element={<CourseProgressPage />} />
            <Route path="/admin/invitations" element={<AdminInvitationsPage />} />
            <Route path="/admin/members" element={<AdminMembersPage />} />
            <Route path="/admin/reviewed" element={<AdminReviewedPage />} />
            <Route path="/admin/reviews" element={<AdminReviewsPage />} />
            <Route path="/admin/reviews/:type/:id" element={<ReviewDetailPage />} />
            <Route path="/admin/reviews/lesson/:id/quiz" element={<AdminReviewLessonQuizPage />} />
          </Route>
        </Route>

        {/* Fallback Catch-All */}
        <Route path="*" element={<Navigate to="/courses" replace />} />
      </Route>
    </Routes>
  );
}
